import React from 'react';
import { C, CARD_W, WINNER_NODE_H, WINNER_IMG_H, normalizeImgField, imgUrl, getPathValue, resolveFeatureIconAndLabel } from '../flowCanvas.constants';

// ─── Wave winner node — Team N winning outcome card with a disruptor slot ─────
export default function WaveWinnerNode({
  step, stepX, stepY, isSelected, isDragOver,
  liveCustomisationData, campaignID, assetFolder, campaignFeatures,
  onActivate, onDragOver, onDragLeave, onDrop, onRemoveDisruptor,
}) {
  const W    = CARD_W;
  const H    = WINNER_NODE_H;
  const left = stepX - W / 2;
  const top  = stepY - H / 2;

  const meta        = step.customisationConfig?.meta || {};
  const teamFilter  = meta.teamWinnerFilter ?? 0;
  const teamNum     = teamFilter + 1;
  const selectLink  = meta.disruptorSelectDataLink;

  // ── Hand image (first item for this team) ──
  const tabKey  = step.customisationConfig?.tabKey;
  const varKey  = step.customisationConfig?.varKey;
  const varName = step.customisationConfig?.variables?.[0]?.variableName;
  const liveVals = liveCustomisationData && tabKey && varKey && varName
    ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName] : null;
  const allHandVals = liveVals || step.populatedData?.variables?.[0]?.allValues || [];
  const hasTeamKey  = Array.isArray(allHandVals) && allHandVals.some(v => v?.team !== undefined);
  const teamItems   = hasTeamKey
    ? allHandVals.filter(v => v?.team === teamFilter || v?.team === 2)
    : Array.isArray(allHandVals) ? allHandVals : (allHandVals ? [allHandVals] : []);
  const firstItem = teamItems[0] || null;
  const imgNorm   = firstItem ? normalizeImgField(firstItem.filename) : { name: null, srcUrl: null, isDefault: true };
  const imgSrc    = imgNorm.srcUrl
    ? imgNorm.srcUrl
    : imgNorm.name ? imgUrl(imgNorm.name, imgNorm.isDefault, assetFolder, campaignID) : null;

  // ── Disruptor slot ──
  const parts     = typeof selectLink === 'string' ? selectLink.split('.') : [];
  const valueKey  = parts.length ? parts[parts.length - 1] : null;
  const groupKeys = parts.length ? parts.slice(0, -1) : [];
  const liveGroup = (liveCustomisationData && groupKeys.length)
    ? getPathValue(liveCustomisationData, groupKeys) : null;
  const disruptorArr = Array.isArray(liveGroup) ? liveGroup : [];
  const disruptorId  = (disruptorArr.length > 0 && valueKey) ? (disruptorArr[0]?.[valueKey] || null) : null;
  const { label: dLabel, icon: dIcon } = resolveFeatureIconAndLabel(disruptorId, campaignFeatures);
  const hasDisruptor = !!disruptorId;

  // ── Layout ──
  const HEADER_H     = 44;
  const IMG_PAD      = 6;
  const DIS_H        = H - HEADER_H - WINNER_IMG_H - 16;
  const headerBottom = top + HEADER_H;
  const imgTop       = headerBottom + 4;
  const disTop       = imgTop + WINNER_IMG_H + 8;

  const outline   = isSelected ? C.sel : isDragOver ? '#a78bfa' : C.purple;
  const clipId    = `fc-winner-clip-${step.stepId}`;
  const checkerId = `fc-winner-checker-${step.stepId}`;

  return (
    <g
      className="fc-node fc-node--winner"
      onClick={onActivate}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{ cursor: 'pointer' }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x={left + IMG_PAD} y={imgTop} width={W - IMG_PAD * 2} height={WINNER_IMG_H} rx="4" />
        </clipPath>
        <pattern id={checkerId} patternUnits="userSpaceOnUse" width="8" height="8">
          <rect width="8" height="8" fill="#555" />
          <rect width="4" height="4" fill="#333" />
          <rect x="4" y="4" width="4" height="4" fill="#333" />
        </pattern>
      </defs>

      {/* Card body */}
      <rect x={left} y={top} width={W} height={H} rx="10"
        fill={isSelected ? C.cardSel : C.card}
        stroke={outline} strokeWidth={isSelected ? 2 : 1.5}
      />
      {/* Header */}
      <text x={left + 12} y={top + 18} fontSize="11" fontWeight="700" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {step.stepTitle || `Winner #${teamNum}`}
      </text>
      <text x={left + 12} y={top + 33} fontSize="9" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        Team {teamNum} · winning outcome
      </text>
      {/* Header separator */}
      <line x1={left + 1} y1={headerBottom} x2={left + W - 1} y2={headerBottom}
        stroke={C.border} strokeWidth="1" />

      {/* Image area – chequered background */}
      <rect x={left + IMG_PAD} y={imgTop} width={W - IMG_PAD * 2} height={WINNER_IMG_H} rx="4"
        fill={`url(#${checkerId})`} />
      {imgSrc ? (
        <image href={imgSrc}
          x={left + IMG_PAD} y={imgTop} width={W - IMG_PAD * 2} height={WINNER_IMG_H}
          preserveAspectRatio="xMidYMid meet"
          clipPath={`url(#${clipId})`}
          style={{ pointerEvents: 'none' }} />
      ) : (
        <>
          <rect x={left + IMG_PAD} y={imgTop} width={W - IMG_PAD * 2} height={WINNER_IMG_H} rx="4"
            fill="none" stroke={C.purpleDim} strokeWidth="1.5" strokeDasharray="4 3" />
          <text x={left + W / 2} y={imgTop + WINNER_IMG_H / 2 - 8}
            textAnchor="middle" dominantBaseline="middle" fontSize="24" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>⊕</text>
          <text x={left + W / 2} y={imgTop + WINNER_IMG_H / 2 + 16}
            textAnchor="middle" fontSize="7" fill={C.textDim}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>Team {teamNum} winning graphic</text>
        </>
      )}

      {/* Disruptor section separator */}
      <line x1={left + 1} y1={disTop - 2} x2={left + W - 1} y2={disTop - 2}
        stroke={C.border} strokeWidth="1" />
      <text x={left + 12} y={disTop + 11} fontSize="8.5" fill={C.textMuted}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>Wave start disruptor (singular)</text>

      {/* Disruptor slot */}
      <rect x={left + 8} y={disTop + 18} width={W - 16} height={DIS_H - 22} rx="6"
        fill={hasDisruptor ? C.circle : C.circle}
        stroke={hasDisruptor ? C.purple : C.border}
        strokeWidth={hasDisruptor ? 1.2 : 1}
        strokeDasharray={hasDisruptor ? 'none' : '4 3'}
      />
      {hasDisruptor ? (
        <>
          <circle cx={left + 8 + 20} cy={disTop + 18 + (DIS_H - 22) / 2} r={14}
            fill="rgba(145,70,255,0.15)" stroke="rgba(145,70,255,0.35)" strokeWidth="1" />
          <text x={left + 8 + 20} y={disTop + 18 + (DIS_H - 22) / 2 + 5}
            textAnchor="middle" fontSize="13"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>{dIcon}</text>
          <text x={left + 8 + 42} y={disTop + 18 + (DIS_H - 22) / 2 + 4}
            fontSize="10" fontWeight="700" fill={C.text}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {dLabel.length > 18 ? dLabel.slice(0, 18) + '…' : dLabel}
          </text>
          {onRemoveDisruptor && (
            <g onClick={e => { e.stopPropagation(); onRemoveDisruptor(step, 0); }} style={{ cursor: 'pointer' }}>
              <circle cx={left + W - 16} cy={disTop + 18 + 12} r={10} fill="#dc2626" />
              <g transform={`translate(${left + W - 16}, ${disTop + 18 + 12})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
              </g>
            </g>
          )}
        </>
      ) : (
        <text x={left + W / 2} y={disTop + 18 + (DIS_H - 22) / 2 + 4}
          textAnchor="middle" fontSize="9" fill={C.textDim}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>Drop wave start disruptor here</text>
      )}
    </g>
  );
}
